<template>
  <div class="client-table-wrapper">
    <table>
      <thead>
        <tr>
          <th
            v-for="column in columns"
            :key="column.key"
            :aria-sort="getAriaSort(column)"
          >
            <button
              v-if="column.sortable"
              type="button"
              class="table-sort-button"
              @click="$emit('sort', column.key)"
            >
              <span>{{ column.label }}</span>
              <span class="sort-indicator" aria-hidden="true">
                {{ getSortIndicator(column.key) }}
              </span>
            </button>

            <template v-else>
              {{ column.label }}
            </template>
          </th>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="client in clients"
          :key="client.clientId ?? client.id"
          class="client-row"
          tabindex="0"
          @click="$emit('select-client', client.clientId)"
          @keydown.enter.prevent="$emit('select-client', client.clientId)"
          @keydown.space.prevent="$emit('select-client', client.clientId)"
        >
          <td v-for="column in columns" :key="column.key">
            <template v-if="column.key === 'clientName'">
              {{ client.clientName || '—' }}
            </template>

            <template v-else-if="column.key === 'milestone'">
              {{ formatMilestone(client) }}
            </template>

            <template v-else-if="column.key === 'customerType'">
              {{ getClientType(client) }}
            </template>

            <template v-else-if="column.key === 'status'">
              <span class="status-pill" :class="getStatusPill(client).class">
                {{ getStatusPill(client).label }}
              </span>
            </template>

            <template v-else-if="column.key === 'goLiveDate'">
              {{ formatDate(client.goLiveDate) }}
            </template>

            <template v-else>
              {{ client[column.key] ?? '—' }}
            </template>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { splitByCapitals } from '@/helpers/strings';
import {
  formatClientDate,
  getClientType,
  getStatusPill,
} from '@/composables/useClientList';

const props = defineProps({
  clients: {
    type: Array,
    default: () => [],
  },
  columns: {
    type: Array,
    required: true,
  },
  sort: {
    type: Object,
    required: true,
  },
});

defineEmits(['select-client', 'sort']);

function formatMilestone(client) {
  const milestone = client?.currentMilestone?.type;
  return milestone ? splitByCapitals(milestone) : '—';
}

function formatDate(value) {
  return formatClientDate(value);
}

function getSortIndicator(key) {
  if (props.sort.key !== key) return '';
  return props.sort.direction === 'asc' ? '↑' : '↓';
}

function getAriaSort(column) {
  if (!column.sortable || props.sort.key !== column.key) return undefined;
  return props.sort.direction === 'asc' ? 'ascending' : 'descending';
}
</script>

<style scoped>
.client-table-wrapper {
  overflow-x: auto;
}

.client-row {
  cursor: pointer;
}

.client-row:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: -2px;
}

.table-sort-button {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-weight: inherit;
  text-align: left;
  cursor: pointer;
}

.sort-indicator {
  min-width: 1rem;
}
</style>
